import { useQuery } from '@tanstack/react-query'
import { billingApi, type BillingSubscription, type FeatureKey } from '@/lib/api'

const FREE_FALLBACK: BillingSubscription = {
  state: 'free',
  planId: null,
  price: null,
  nextBillingDate: null,
  trialEndsAt: null,
  cancelAtPeriodEnd: false,
}

export function useSubscription() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription(),
    retry: 1,
    staleTime: 5 * 60_000,
  })

  // On error default to free; null while loading so gates don't flash
  const subscription = data ?? (isError ? FREE_FALLBACK : null)
  const isPremium =
    subscription?.state === 'active' || subscription?.state === 'trialing'

  return { subscription, isLoading, isPremium }
}

export interface FeatureGateResult {
  /** Whether the feature is accessible. */
  allowed: boolean
  /** Remaining free uses for metered features; null if unlimited or hard gate. */
  remainingUses: number | null
  /** True while subscription loads — don't flash gates during load. */
  isLoading: boolean
}

const HARD_GATE_FEATURES: FeatureKey[] = [
  'compatibility',
  'remedies',
  'insights',
  'event_muhurta',
]

export const MUHURTA_FREE_LIMIT = 3

export function useFeatureGate(
  feature: FeatureKey,
  muhurtaUsedThisMonth?: number,
): FeatureGateResult {
  const { isLoading, isPremium } = useSubscription()

  if (isLoading) {
    return { allowed: true, remainingUses: null, isLoading: true }
  }

  if (isPremium) {
    return { allowed: true, remainingUses: null, isLoading: false }
  }

  if (feature === 'muhurta') {
    const used = muhurtaUsedThisMonth ?? 0
    const remaining = Math.max(0, MUHURTA_FREE_LIMIT - used)
    return { allowed: remaining > 0, remainingUses: remaining, isLoading: false }
  }

  if (feature === 'dasha_full') {
    return { allowed: false, remainingUses: null, isLoading: false }
  }

  if (HARD_GATE_FEATURES.includes(feature)) {
    return { allowed: false, remainingUses: null, isLoading: false }
  }

  return { allowed: true, remainingUses: null, isLoading: false }
}
